import { create } from "zustand";

import { defaultEditorState } from "../lib/image-processing";
import type { ModelSummaryResponse, PredictionResponse } from "../types/api";
import type { AspectPreset, CropAreaPixels, EditorState, RenderedImage, UploadedImage } from "../types/image";

type RequestState = "idle" | "loading" | "success" | "error";

interface HorizonStore {
  originalImage: UploadedImage | null;
  renderedImage: RenderedImage | null;
  editorState: EditorState;
  renderStatus: RequestState;
  renderError: string | null;
  modelSummary: ModelSummaryResponse | null;
  modelSummaryStatus: RequestState;
  modelSummaryError: string | null;
  predictionStatus: RequestState;
  predictionMessage: string | null;
  predictionError: string | null;
  predictionResult: PredictionResponse | null;
  predictionPreviewUrl: string | null;
  predictionSource: "original" | "edited" | null;
  setOriginalImage: (image: UploadedImage) => void;
  clearSession: () => void;
  setCropPosition: (crop: EditorState["crop"]) => void;
  setCropAreaPixels: (cropAreaPixels: CropAreaPixels) => void;
  setZoom: (zoom: number) => void;
  setRotation: (rotation: number) => void;
  setFlipHorizontal: () => void;
  setFlipVertical: () => void;
  setBrightness: (value: number) => void;
  setContrast: (value: number) => void;
  setSaturation: (value: number) => void;
  setBlur: (value: number) => void;
  setSharpen: (value: number) => void;
  setAspectPreset: (value: AspectPreset) => void;
  toggleCompareMode: () => void;
  resetEdits: () => void;
  setRenderedImage: (image: RenderedImage | null) => void;
  setRenderStatus: (status: RequestState, error?: string | null) => void;
  setModelSummary: (summary: ModelSummaryResponse) => void;
  setModelSummaryError: (message: string) => void;
  setModelSummaryLoading: () => void;
  setPredictionLoading: (message: string) => void;
  setPredictionSuccess: (result: PredictionResponse, previewUrl: string, source: "original" | "edited") => void;
  setPredictionError: (message: string) => void;
  resetPrediction: () => void;
}

function revokeImageUrl(url: string | null | undefined): void {
  if (url) {
    URL.revokeObjectURL(url);
  }
}

function revokePredictionPreviewIfOwned(url: string | null | undefined, originalUrl?: string, renderedUrl?: string): void {
  if (!url) {
    return;
  }

  if (url !== originalUrl && url !== renderedUrl) {
    URL.revokeObjectURL(url);
  }
}

export const useHorizonStore = create<HorizonStore>((set, get) => ({
  originalImage: null,
  renderedImage: null,
  editorState: defaultEditorState,
  renderStatus: "idle",
  renderError: null,
  modelSummary: null,
  modelSummaryStatus: "idle",
  modelSummaryError: null,
  predictionStatus: "idle",
  predictionMessage: null,
  predictionError: null,
  predictionResult: null,
  predictionPreviewUrl: null,
  predictionSource: null,
  setOriginalImage: (image) => {
    const currentState = get();
    revokePredictionPreviewIfOwned(
      currentState.predictionPreviewUrl,
      currentState.originalImage?.objectUrl,
      currentState.renderedImage?.objectUrl
    );
    revokeImageUrl(currentState.originalImage?.objectUrl);
    revokeImageUrl(currentState.renderedImage?.objectUrl);
    set({
      originalImage: image,
      renderedImage: null,
      editorState: defaultEditorState,
      renderStatus: "idle",
      renderError: null,
      predictionStatus: "idle",
      predictionMessage: null,
      predictionError: null,
      predictionResult: null,
      predictionPreviewUrl: null,
      predictionSource: null
    });
  },
  clearSession: () => {
    const currentState = get();
    revokeImageUrl(currentState.predictionPreviewUrl);
    revokeImageUrl(currentState.originalImage?.objectUrl);
    revokeImageUrl(currentState.renderedImage?.objectUrl);
    set({
      originalImage: null,
      renderedImage: null,
      editorState: defaultEditorState,
      renderStatus: "idle",
      renderError: null,
      predictionStatus: "idle",
      predictionMessage: null,
      predictionError: null,
      predictionResult: null,
      predictionPreviewUrl: null,
      predictionSource: null
    });
  },
  setCropPosition: (crop) => set((state) => ({ editorState: { ...state.editorState, crop } })),
  setCropAreaPixels: (cropAreaPixels) => set((state) => ({ editorState: { ...state.editorState, cropAreaPixels } })),
  setZoom: (zoom) => set((state) => ({ editorState: { ...state.editorState, zoom } })),
  setRotation: (rotation) => set((state) => ({ editorState: { ...state.editorState, rotation } })),
  setFlipHorizontal: () =>
    set((state) => ({ editorState: { ...state.editorState, flipHorizontal: !state.editorState.flipHorizontal } })),
  setFlipVertical: () =>
    set((state) => ({ editorState: { ...state.editorState, flipVertical: !state.editorState.flipVertical } })),
  setBrightness: (brightness) => set((state) => ({ editorState: { ...state.editorState, brightness } })),
  setContrast: (contrast) => set((state) => ({ editorState: { ...state.editorState, contrast } })),
  setSaturation: (saturation) => set((state) => ({ editorState: { ...state.editorState, saturation } })),
  setBlur: (blur) => set((state) => ({ editorState: { ...state.editorState, blur } })),
  setSharpen: (sharpen) => set((state) => ({ editorState: { ...state.editorState, sharpen } })),
  setAspectPreset: (aspectPreset) => set((state) => ({ editorState: { ...state.editorState, aspectPreset } })),
  toggleCompareMode: () =>
    set((state) => ({
      editorState: {
        ...state.editorState,
        compareMode: state.editorState.compareMode === "before" ? "after" : "before"
      }
    })),
  resetEdits: () =>
    set({
      editorState: defaultEditorState,
      renderStatus: "idle",
      renderError: null
    }),
  setRenderedImage: (image) => {
    revokeImageUrl(get().renderedImage?.objectUrl);
    set({ renderedImage: image });
  },
  setRenderStatus: (status, error = null) => set({ renderStatus: status, renderError: error }),
  setModelSummary: (summary) =>
    set({
      modelSummary: summary,
      modelSummaryStatus: "success",
      modelSummaryError: null
    }),
  setModelSummaryError: (message) =>
    set({
      modelSummaryStatus: "error",
      modelSummaryError: message
    }),
  setModelSummaryLoading: () =>
    set((state) => ({
      modelSummaryStatus: state.modelSummary ? "success" : "loading",
      modelSummaryError: null
    })),
  setPredictionLoading: (message) =>
    set({
      predictionStatus: "loading",
      predictionMessage: message,
      predictionError: null
    }),
  setPredictionSuccess: (result, previewUrl, source) =>
    set((state) => {
      revokePredictionPreviewIfOwned(
        state.predictionPreviewUrl,
        state.originalImage?.objectUrl,
        state.renderedImage?.objectUrl
      );
      return {
      predictionStatus: "success",
      predictionMessage: null,
      predictionError: null,
      predictionResult: result,
      predictionPreviewUrl: previewUrl,
      predictionSource: source
      };
    }),
  setPredictionError: (message) =>
    set({
      predictionStatus: "error",
      predictionMessage: null,
      predictionError: message,
      predictionResult: null
    }),
  resetPrediction: () =>
    set((state) => {
      revokePredictionPreviewIfOwned(
        state.predictionPreviewUrl,
        state.originalImage?.objectUrl,
        state.renderedImage?.objectUrl
      );
      return {
      predictionStatus: "idle",
      predictionMessage: null,
      predictionError: null,
      predictionResult: null,
      predictionPreviewUrl: null,
      predictionSource: null
      };
    })
}));
